import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import "./SelfieCapture.css";

const WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

/** Blendshape score near 1 = eye closed; keep below this to treat as open. */
const EYE_BLINK_MAX = 0.45;
/** Minimum eye contour size vs face box — filters too-far / blurry faces. */
const MIN_EYE_SPAN_RATIO = 0.045;

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const getBlendscore = (blendshapes, name) => {
  const list = blendshapes?.[0]?.categories || [];
  const hit = list.find((c) => c.categoryName === name);
  return hit ? hit.score : 1;
};

/**
 * Require one face, both eyes open, and eye landmarks large enough
 * that the iris / eye region is actually visible in frame.
 */
const evaluateFace = (result) => {
  if (!result?.faceLandmarks?.length) {
    return { ok: false, message: "Face not detected. Look at the camera." };
  }

  if (result.faceLandmarks.length > 1) {
    return {
      ok: false,
      message: "Multiple faces detected. Only one face allowed.",
    };
  }

  const landmarks = result.faceLandmarks[0];
  const blinkL = getBlendscore(result.faceBlendshapes, "eyeBlinkLeft");
  const blinkR = getBlendscore(result.faceBlendshapes, "eyeBlinkRight");

  if (blinkL > EYE_BLINK_MAX || blinkR > EYE_BLINK_MAX) {
    return {
      ok: false,
      message: "Keep both eyes open — eyes/retina must be clearly visible.",
    };
  }

  // Left eye: 33–133, Right eye: 362–263 (MediaPipe face mesh)
  const leftSpan = dist(landmarks[33], landmarks[133]);
  const rightSpan = dist(landmarks[362], landmarks[263]);
  const faceWidth = dist(landmarks[234], landmarks[454]);

  if (!faceWidth || leftSpan / faceWidth < MIN_EYE_SPAN_RATIO) {
    return {
      ok: false,
      message: "Come closer — left eye/retina is not clear enough.",
    };
  }

  if (rightSpan / faceWidth < MIN_EYE_SPAN_RATIO) {
    return {
      ok: false,
      message: "Come closer — right eye/retina is not clear enough.",
    };
  }

  return { ok: true, message: "Eyes visible. You can take the photo." };
};

const SelfieCapture = ({ show, handleClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);

  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState(null);
  const [modelsReady, setModelsReady] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [eyesOk, setEyesOk] = useState(false);
  const [faceHint, setFaceHint] = useState("Loading face check...");
  const [faceError, setFaceError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadModel = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(WASM_URL);
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_URL,
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
        }).catch(async () =>
          FaceLandmarker.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath: MODEL_URL,
              delegate: "CPU",
            },
            runningMode: "VIDEO",
            numFaces: 1,
            outputFaceBlendshapes: true,
          })
        );

        if (cancelled) {
          landmarker.close();
          return;
        }

        landmarkerRef.current = landmarker;
        setModelsReady(true);
        setFaceHint("Position your face in the frame");
      } catch (err) {
        console.error("FaceLandmarker load error:", err);
        if (!cancelled) {
          setModelsReady(false);
          setFaceHint("Face check failed to load. Please refresh and try again.");
        }
      }
    };

    loadModel();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const runFaceLoop = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;

    if (!video || !landmarker || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(runFaceLoop);
      return;
    }

    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const result = landmarker.detectForVideo(video, performance.now());
        const verdict = evaluateFace(result);
        setEyesOk(verdict.ok);
        setFaceHint(verdict.message);
        if (verdict.ok) setFaceError("");
      } catch (err) {
        console.warn("detectForVideo error:", err);
      }
    }

    rafRef.current = requestAnimationFrame(runFaceLoop);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      stopCamera();
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      lastVideoTimeRef.current = -1;
      rafRef.current = requestAnimationFrame(runFaceLoop);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setFaceError("Camera access denied. Please allow camera permission.");
      setEyesOk(false);
      setCameraReady(false);
    }
  }, [runFaceLoop, stopCamera]);

  useEffect(() => {
    if (show && !selfiePreviewUrl) {
      startCamera();
    } else if (!show) {
      stopCamera();
      setSelfiePreviewUrl(null);
      setEyesOk(false);
      setFaceError("");
      setFaceHint(
        modelsReady ? "Position your face in the frame" : "Loading face check..."
      );
    }

    return () => {
      if (!show) stopCamera();
    };
  }, [show, selfiePreviewUrl, startCamera, stopCamera, modelsReady]);

  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    if (!modelsReady) {
      setFaceError("Face detection is still loading. Please wait...");
      return;
    }

    if (!eyesOk) {
      setFaceError(
        faceHint ||
          "Eyes/retina not visible. Keep both eyes open and look at the camera."
      );
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    setFaceError("");
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const imageUrl = canvas.toDataURL("image/png");
    setSelfiePreviewUrl(imageUrl);
    stopCamera();
  };

  const handleRetake = () => {
    setSelfiePreviewUrl(null);
    setFaceError("");
    setEyesOk(false);
    setFaceHint("Position your face in the frame");
  };

  const handleUsePhoto = () => {
    if (selfiePreviewUrl) {
      onCapture(selfiePreviewUrl);
      stopCamera();
      handleClose();
    }
  };

  const handleModalClose = () => {
    stopCamera();
    setSelfiePreviewUrl(null);
    setFaceError("");
    handleClose();
  };

  const canTakePhoto = modelsReady && eyesOk && cameraReady;

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Capture Selfie</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {!selfiePreviewUrl && (
          <div className="selfie-video-wrap">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`selfie-video ${eyesOk ? "is-eyes-ok" : "is-eyes-bad"}`}
            />
            <span
              className={`selfie-eye-badge ${eyesOk ? "is-ok" : "is-bad"}`}
              aria-live="polite"
            >
              {eyesOk ? "Eyes OK" : "Eyes required"}
            </span>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />

        {selfiePreviewUrl && (
          <img
            src={selfiePreviewUrl}
            alt="Selfie Preview"
            style={{ width: "100%", maxWidth: "400px", borderRadius: "12px" }}
          />
        )}

        {!selfiePreviewUrl && (
          <p
            className={`selfie-face-hint ${eyesOk ? "is-ok" : ""}`}
            aria-live="polite"
          >
            {faceHint}
          </p>
        )}

        {faceError && <p className="selfie-face-error">{faceError}</p>}

        <p className="mt-2 selfie-guide-text">
          Keep both eyes open and clearly visible. Photo is allowed only when
          eyes/retina are detected.
        </p>
      </Modal.Body>
      <Modal.Footer>
        {!selfiePreviewUrl && (
          <>
            <Button variant="" onClick={handleModalClose} className="cancel-btn">
              Cancel
            </Button>
            <Button
              variant=""
              onClick={captureSelfie}
              disabled={!canTakePhoto}
              className="selfie-btn"
              title={
                canTakePhoto
                  ? "Take photo"
                  : "Waiting for both eyes to be clearly visible"
              }
            >
              {!modelsReady
                ? "Loading..."
                : eyesOk
                  ? "Take Photo"
                  : "Eyes not clear"}
            </Button>
          </>
        )}

        {selfiePreviewUrl && (
          <>
            <Button variant="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button variant="warning" onClick={handleRetake}>
              Retake
            </Button>
            <Button variant="success" onClick={handleUsePhoto}>
              Use Photo
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SelfieCapture;
